import { Div, Button, Span } from '@codesuma/baseline'
import style from './index.module.css'

// The day page's tabs: what the day holds — its tasks, its events and the
// occasions it is named for — what was written about it, and the colour the
// reader gave it.
export type DayTab = 'agenda' | 'notes' | 'color'

const LABELS: Record<DayTab, string> = {
    agenda: 'کارها و رویدادها',
    notes: 'یادداشت روز',
    // Said rather than shown: the stop itself is a small coloured disc.
    color: 'رنگ روز',
}

const TABS: DayTab[] = ['agenda', 'notes', 'color']

// Where a stop stands in the pill, in physical px from its left edge.
interface Stop {
    id: DayTab
    left: number
    width: number
}

// The tabs of the day page, as the home page's calendar switch: a pill along
// the bottom, and a thumb that slides to the one picked. It rides on the page
// rather than in a slide, because it names what every slide is showing and has
// to stay put while the days pass under it.
//
// One pointer gesture handles everything, as on the switch: a tap picks the
// tapped stop, a drag carries the thumb and lets it go to the nearest one. The
// stops are as wide as their labels, so where the thumb goes and how wide it
// is are measured off them rather than worked out by thirds. offsetLeft is
// physical — from the pill's left edge whichever way it reads — and so is
// translateX, so the gesture is direction-free.
//
// Emits 'pick' with the tab the gesture landed on; the page decides what to do
// with it and calls select() once it has. Nothing here changes on its own.
export const TabBar = (initial: DayTab) => {
    const base = Div()
    base.addClass(style.base)

    const pill = Div()
    pill.addClass(style.pill)
    pill.attr('role', 'tablist')
    base.append(pill)

    const thumb = Div()
    thumb.addClass(style.thumb)
    pill.append(thumb)

    let active = initial
    const buttons = new Map<DayTab, ReturnType<typeof Button>>()
    for (const id of TABS) {
        const tab = Button(id === 'color' ? undefined : LABELS[id])
        tab.addClass(style.option)
        tab.attr('role', 'tab')
        // No words on the colour stop, only the disc; its name is for the ear.
        if (id === 'color') {
            tab.addClass(style.colorStop)
            const dot = Span()
            dot.addClass(style.colorDot)
            tab.append(dot)
            tab.attr('aria-label', LABELS[id])
        }
        // A tap, and the keyboard. A drag captures the pointer on the pill
        // before it lets go, so this never fires for one.
        tab.on('click', () => base.emit('pick', id))
        buttons.set(id, tab)
        pill.append(tab)
    }

    // Left to right, whichever way the labels read.
    const measure = (): Stop[] => TABS
        .map(id => ({ id, left: buttons.get(id)!.el.offsetLeft, width: buttons.get(id)!.el.offsetWidth }))
        .sort((a, b) => a.left - b.left)

    const draw = (left: number, width: number, animate: boolean) => thumb.style({
        transition: animate ? '' : 'none',
        width: `${width}px`,
        transform: `translateX(${left}px)`,
    })

    // The thumb onto the active stop. The first placing is a jump: there is
    // nothing yet to slide from.
    let placed = false
    const place = () => {
        const el = buttons.get(active)!.el
        if (!el.offsetWidth) return // not laid out yet; the observer calls back
        draw(el.offsetLeft, el.offsetWidth, placed)
        placed = true
    }
    // Fires once the pill is first laid out, and again whenever the text size
    // reshapes it — both times the thumb has to be measured afresh.
    new ResizeObserver(place).observe(pill.el)

    // The drag. The thumb's width follows its position, easing from one
    // stop's width to the next so it fits whatever it is passing over.
    let pointerId: number | null = null
    let startX = 0
    let startLeft = 0
    let stops: Stop[] = []
    let dragging = false
    let x = 0 // the thumb's left while dragging

    const widthAt = (left: number) => {
        for (let i = 1; i < stops.length; i++) {
            const a = stops[i - 1], b = stops[i]
            if (left <= b.left) {
                const t = (left - a.left) / (b.left - a.left)
                return a.width + t * (b.width - a.width)
            }
        }
        return stops[stops.length - 1].width
    }

    const nearest = () => {
        const centre = x + widthAt(x) / 2
        let best = stops[0]
        for (const s of stops) {
            if (Math.abs(s.left + s.width / 2 - centre) < Math.abs(best.left + best.width / 2 - centre)) best = s
        }
        return best.id
    }

    pill.on('pointerdown', (e: PointerEvent) => {
        if (pointerId !== null) return
        pointerId = e.pointerId
        startX = e.clientX
        stops = measure()
        startLeft = buttons.get(active)!.el.offsetLeft
        dragging = false
    })

    pill.on('pointermove', (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return
        if (!dragging) {
            if (Math.abs(e.clientX - startX) <= 4) return
            dragging = true
            // From here on the pointer is the pill's, and the tap below it
            // will not click — a drag is not a tap.
            pill.el.setPointerCapture(e.pointerId)
        }
        const first = stops[0].left
        const last = stops[stops.length - 1].left
        x = Math.min(last, Math.max(first, startLeft + (e.clientX - startX)))
        draw(x, widthAt(x), false)
    })

    pill.on('pointerup', (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return
        pointerId = null
        if (!dragging) return // a tap: the stop's own click handles it
        const id = nearest()
        if (id !== active) base.emit('pick', id)
        else place() // no change: just slide the thumb back
    })

    pill.on('pointercancel', (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return
        pointerId = null
        if (dragging) place()
    })

    const select = (id: DayTab) => {
        active = id
        for (const [key, tab] of buttons) {
            tab.toggleClass(style.active, key === id)
            tab.attr('aria-selected', key === id ? 'true' : 'false')
        }
        place()
    }
    select(initial)

    return Object.assign(base, { select })
}
